import React, { Suspense, lazy } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { AbilityProvider } from './contexts/AbilityContext'
import { ConsentFlowProvider } from './contexts/ConsentFlowContext'
import AppLayout from './components/AppLayout'
import PageSkeleton from './components/PageSkeleton'
import './index.css'

// Lazy-loaded page components
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const BuilderPage = lazy(() => import('./pages/BuilderPage'))
const PreviewPage = lazy(() => import('./pages/PreviewPage'))
const PatientsPage = lazy(() => import('./pages/PatientsPage'))
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const SignupPage = lazy(() => import('./pages/SignupPage'))
const InviteSignupPage = lazy(() => import('./pages/InviteSignupPage'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const SigningSuccessPage = lazy(() => import('./pages/SigningSuccessPage'))
const SigningErrorPage = lazy(() => import('./pages/SigningErrorPage'))
const PatientSigningPage = lazy(() => import('./pages/PatientSigningPage'))

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public route wrapper (redirect to app if logged in)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
}

const SuspenseFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
  </div>
)

function AppRoutes() {
  return (
    <Suspense fallback={<SuspenseFallback />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
        <Route path="/invite/:token" element={<PublicRoute><InviteSignupPage /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />

        {/* Patient signing - public, no auth */}
        <Route path="/sign/:token" element={<PatientSigningPage />} />

        {/* Signing callbacks */}
        <Route path="/signing-success" element={<SigningSuccessPage />} />
        <Route path="/signing-error" element={<SigningErrorPage />} />

        {/* Protected routes with AppLayout */}
        <Route element={
          <ProtectedRoute>
            <ConsentFlowProvider>
              <AppLayout />
            </ConsentFlowProvider>
          </ProtectedRoute>
        }>
          <Route index element={
            <Suspense fallback={<PageSkeleton />}><DashboardPage /></Suspense>
          } />
          <Route path="builder" element={
            <Suspense fallback={<PageSkeleton />}><BuilderPage /></Suspense>
          } />
          <Route path="preview" element={
            <Suspense fallback={<PageSkeleton />}><PreviewPage /></Suspense>
          } />
          <Route path="patients" element={
            <Suspense fallback={<PageSkeleton />}><PatientsPage /></Suspense>
          } />
          <Route path="templates" element={
            <Suspense fallback={<PageSkeleton />}><TemplatesPage /></Suspense>
          } />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
})

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AbilityProvider>
            <AppRoutes />
          </AbilityProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
    </QueryClientProvider>
  </React.StrictMode>,
)
